import { Plugin, PluginKey } from '@tiptap/pm/state';
import { DecorationSet } from '@tiptap/pm/view';

export const AutoPaginationPluginKey = new PluginKey('autoPagination');

interface PaginationConfig {
    pageHeight: number; // in pixels
    enabled: boolean;
}

export function createAutoPaginationPlugin(config: PaginationConfig) {
    let lastContentHeight = 0;
    let isProcessing = false;

    return new Plugin({
        key: AutoPaginationPluginKey,
        
        state: {
            init() {
                return DecorationSet.empty;
            },
            apply(tr, set) {
                return set.map(tr.mapping, tr.doc);
            },
        },

        view(editorView) {
            const checkPagination = () => {
                if (!config.enabled || isProcessing) return;

                const editorElement = editorView.dom;
                const contentHeight = editorElement.scrollHeight;

                // Only process if height changed significantly (more than 50px)
                if (Math.abs(contentHeight - lastContentHeight) < 50) {
                    return;
                }

                lastContentHeight = contentHeight;
                const pagesNeeded = Math.ceil(contentHeight / config.pageHeight);


                if (pagesNeeded > 1) {
                    isProcessing = true;
                    
                    // Find where to insert page breaks
                    setTimeout(() => {
                        try {
                            insertPageBreaks(editorView, config.pageHeight, pagesNeeded);
                        } finally {
                            isProcessing = false;
                        }
                    }, 500); // Debounce
                }
            };

            // Check on initial load and when view updates
            setTimeout(checkPagination, 100);

            return {
                update(view, prevState) {
                    if (view.state.doc !== prevState.doc) {
                        checkPagination();
                    }
                },
            };
        },
    });
}

function insertPageBreaks(view: any, pageHeight: number, pagesNeeded: number) {
    const { state, dispatch } = view;
    const { doc, tr } = state;

    // Remove existing page break spacers
    const toDelete: { from: number; to: number }[] = [];
    doc.descendants((node: any, pos: number) => {
        if (node.type.name === 'pageBreakSpacer') {
            toDelete.push({ from: pos, to: pos + node.nodeSize });
        }
    });

    // Delete from back to front to avoid position shifts
    let transaction = tr;
    for (let i = toDelete.length - 1; i >= 0; i--) {
        transaction = transaction.delete(toDelete[i].from, toDelete[i].to);
    }

    // Now calculate where new breaks should go
    // This is a simplified approach - measure DOM and find corresponding doc positions
    let accumulatedHeight = 0;
    const breakPositions: Array<{ pos: number; height: number }> = [];

    // Walk through the document and find positions where we cross page boundaries
    doc.descendants((node: any, pos: number) => {
        if (node.isBlock && node.type.name !== 'pageBreakSpacer') {
            // Try to find the corresponding DOM node
            const domNode = view.nodeDOM(pos);
            if (domNode && domNode instanceof HTMLElement) {
                const nodeHeight = domNode.offsetHeight;
                const nodeTop = accumulatedHeight;
                const nodeBottom = accumulatedHeight + nodeHeight;

                // Check if this node crosses a page boundary
                for (let page = 1; page < pagesNeeded; page++) {
                    const breakPoint = page * pageHeight;
                    if (nodeTop < breakPoint && nodeBottom > breakPoint) {
                        // Calculate how much space is left on current page
                        const spaceLeftOnPage = breakPoint - nodeTop;
                        // Add gap between pages (30px) + space needed to reach next page
                        const spacerHeight = spaceLeftOnPage + 30 + 120; // 120 = top+bottom padding
                        
                        breakPositions.push({ pos, height: spacerHeight });
                        break;
                    }
                }

                accumulatedHeight += nodeHeight;
            }
        }
    });

    // Insert page break spacers at the identified positions
    // Insert from back to front to avoid position shifts
    for (let i = breakPositions.length - 1; i >= 0; i--) {
        const { pos, height } = breakPositions[i];
        const spacerNode = state.schema.nodes.pageBreakSpacer.create({ height });
        transaction = transaction.insert(pos, spacerNode);
    }

    if (transaction.steps.length > 0) {
        dispatch(transaction);
    }
}
